"use client";

import { ReactNode, useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

type HorizontalCarouselProps = {
  children: ReactNode;
  className?: string;
  showFade?: boolean;
  showNext?: boolean;
  showPrevious?: boolean;
  watchDrag?: boolean;
  setApi?: (api: CarouselApi) => void;
  edgeFadeClassName?: string;
};

const HorizontalCarousel = ({
  children,
  className,
  showFade = true,
  showNext = true,
  showPrevious = true,
  watchDrag = true,
  setApi,
  edgeFadeClassName,
}: HorizontalCarouselProps) => {
  const [api, setInternalApi] = useState<CarouselApi>();
  const [canScrollNext, setCanScrollNext] = useState(true);

  const handleApi = useCallback(
    (nextApi: CarouselApi) => {
      setInternalApi(nextApi);
      setApi?.(nextApi);
    },
    [setApi],
  );

  useEffect(() => {
    if (!api) return;

    const updateScrollState = () => setCanScrollNext(api.canScrollNext());

    updateScrollState();
    api.on("select", updateScrollState);
    api.on("reInit", updateScrollState);

    return () => {
      api.off("select", updateScrollState);
      api.off("reInit", updateScrollState);
    };
  }, [api]);

  return (
    <Carousel
      setApi={handleApi}
      opts={{
        align: "start",
        loop: false,
        slidesToScroll: 1,
        watchDrag,
      }}
      className={cn(
        "relative w-full cursor-grab active:cursor-grabbing",
        className,
      )}
    >
      <div className="relative">
        <CarouselContent className="relative md:gap-4 gap-2 pb-5">
          {children}
        </CarouselContent>

        {showFade && canScrollNext && (
          <div
            className={cn(
              "pointer-events-none absolute inset-y-0 right-0 z-10 w-11 bg-gradient-to-l to-transparent",
              edgeFadeClassName ?? "from-subtle/95",
            )}
          />
        )}
      </div>

      {showPrevious && (
        <CarouselPrevious className="-left-0 z-20 bg-white border shadow-2xl text-heading" />
      )}
      {showNext && (
        <CarouselNext className="-right-0 z-20 bg-white border shadow-2xl text-heading" />
      )}
    </Carousel>
  );
};

export default HorizontalCarousel;
